import { useEffect, useState } from 'react'
import { ChatApiError, checkChatResponse } from '../../apiErrors'
import { modelTestCopy, modelTestError } from './copy'
import { ModelCatalog } from './types'

export function useModelCatalog(
  enabled: boolean,
  chatServer: string,
  getToken: () => Promise<string | null>,
) {
  const [catalog, setCatalog] = useState<ModelCatalog | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [unavailableStatus, setUnavailableStatus] = useState<number | null>(
    null,
  )
  const [revision, setRevision] = useState(0)

  useEffect(() => {
    setCatalog(null)
    setError(null)
    setUnavailableStatus(null)
    if (!enabled) return
    const controller = new AbortController()
    const load = async () => {
      try {
        const token = await getToken()
        if (controller.signal.aborted) return
        if (!token) throw new Error('No access token')
        const response = await fetch(`${chatServer}/models`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        })
        await checkChatResponse(response)
        const result: ModelCatalog = await response.json()
        if (!controller.signal.aborted) setCatalog(result)
      } catch (error) {
        if (!controller.signal.aborted) {
          if (
            error instanceof ChatApiError &&
            [403, 404, 409].includes(error.status)
          ) {
            setUnavailableStatus(error.status)
          } else {
            setError(modelTestError(error, modelTestCopy.catalogFailed))
          }
        }
      }
    }
    void load()
    return () => controller.abort()
  }, [enabled, chatServer, getToken, revision])

  return {
    catalog,
    error,
    unavailableStatus,
    reload: () => setRevision((value) => value + 1),
  }
}
